import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import TermsReacceptGate from "@/components/TermsReacceptGate";
import PrivacyReacceptGate from "@/components/PrivacyReacceptGate";
import ProviderAgreementReacceptGate from "@/components/ProviderAgreementReacceptGate";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import Index from "./pages/Index";

// Lazy-load non-homepage routes to keep the initial JS bundle small.
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Register = lazy(() => import("./pages/Register"));
const SearchPage = lazy(() => import("./pages/Search"));
const ProviderProfile = lazy(() => import("./pages/ProviderProfile"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminVehicles = lazy(() => import("./pages/AdminVehicles"));
const About = lazy(() => import("./pages/About"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const ProviderAgreement = lazy(() => import("./pages/ProviderAgreement"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const RiderTerms = lazy(() => import("./pages/RiderTerms"));
const CodeOfConduct = lazy(() => import("./pages/CodeOfConduct"));
const CCPA = lazy(() => import("./pages/CCPA"));
const Refunds = lazy(() => import("./pages/Refunds"));
const Contact = lazy(() => import("./pages/Contact"));
const Premium = lazy(() => import("./pages/Premium"));
const CityLanding = lazy(() => import("./pages/CityLanding"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <TermsReacceptGate />
          <PrivacyReacceptGate />
          <ProviderAgreementReacceptGate />
          <CookieConsentBanner />
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/register" element={<Register />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/provider/:id" element={<ProviderProfile />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/vehicles" element={<AdminVehicles />} />
              <Route path="/about" element={<About />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/provider-agreement" element={<ProviderAgreement />} />
              <Route path="/cookies" element={<Cookies />} />
              <Route path="/disclaimer" element={<Disclaimer />} />
              <Route path="/rider-terms" element={<RiderTerms />} />
              <Route path="/code-of-conduct" element={<CodeOfConduct />} />
              <Route path="/ccpa" element={<CCPA />} />
              <Route path="/refunds" element={<Refunds />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/premium" element={<Premium />} />
              <Route path="/cities/:slug" element={<CityLanding />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/checkout/return" element={<CheckoutReturn />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
